import {
  createMeetingsContext,
  MeetingContextData,
  MeetingSessionOvenMediaEmitterManager,
  MeetingSessionOvenMediaReceiverManager,
  MeetingSource,
  MeetingSourceDeviceType,
  useMeetingContextDataOnChange,
  useMeetingContextDataSelector,
  VideoSignalPreset
} from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { destroyMediaStream, MediaStreamEmptyVideoTrack, MediaStreamSilentTrack, useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import {
  OvenMediaEmitterWebRTCSessionOptions,
  OvenMediaReceiverWebRTCSessionOptions,
  RTCStreamParams
} from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingAccessibilityOutputDTO, VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import {
  Meeting,
  MeetingPermissions,
  MeetingRoomStatus,
  MeetingRoomType,
  MeetingSession,
  MeetingSessionAddMedia,
  MeetingSessionMedia
} from "@kalyzee/kast-web-sdk/websocket";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSdkContext } from "../contexts/sdk/context";
import { getAudioContextForVolumeAnalyzer } from "../helpers/audio";
import { keepArrayIdentity } from "../helpers/identity";
import { toastError } from "../helpers/toast";
import { RequiredSome } from "../interfaces/utils";
import { useSettings } from "./settings";

export interface MeetingSourceControls {
  type: "aver-ptz";
  hostname?: string;
  username?: string;
  password?: string;
  [key: string]: any;
}

export type MeetingSourceType = "camera" | "screen";

/**
 * How the player renders the video: the CSS `object-fit` values, plus
 * `interactive` which enables the crop + minimap navigation. Defaults to
 * `cover` when unset.
 */
export type PlayerFit = "fill" | "contain" | "cover" | "none" | "scale-down" | "interactive";

/**
 * Per-source config carried onto the media session's `extra` (and from there,
 * through the recording chain, onto the VOD's `extra`). `width`/`height` are
 * the source's known content dimensions (e.g. a wide camera), so consumers get
 * the ratio at t0 without having to measure the stream.
 */
export type MeetingSourceExtended = RequiredSome<
  MeetingSource<any, { type: MeetingSourceType; playerFit?: PlayerFit; width?: number; height?: number }>,
  "extra"
> & {
  controls?: MeetingSourceControls;
  visible?: boolean;
  /**
   * Watch the stream for an actual video signal (e.g. nothing plugged into a
   * capture card) and hide the media while there is none. Off by default: the
   * detection only makes sense on sources whose "no signal" frame is known,
   * hence the preset.
   */
  signalDetection?: {
    enabled?: boolean;
    preset?: VideoSignalPreset;
    /** Sampling interval in ms. Overrides the preset's own value. */
    interval?: number;
    /** Consecutive samples without signal before hiding the media. */
    noSignalConfirmations?: number;
    /** Consecutive samples with signal before showing it again. */
    signalConfirmations?: number;
  };
  /**
   * Audio level meter drawn over our own players of this source, over the
   * component defaults. Volumes are on the analyzer's 0-255 byte scale, which
   * is mapped from decibels: a quiet room does not read 0 but its noise floor,
   * so `minVolume` is what tells the meter where silence starts — and with it
   * the no-sound warning, whose threshold follows `minVolume`.
   */
  audioLevel?: {
    disabled?: boolean;
    /** Exponent bending the 0-1 level before it is drawn. Defaults to 2. */
    boost?: number;
    /** Level read as silence, below which the bar stays empty. Defaults to 0. */
    minVolume?: number;
    /** Level read as full scale. Defaults to 220. */
    maxVolume?: number;
    /**
     * Warn under the meter while the source has never risen above `minVolume`.
     * Going quiet mid-conversation never raises it.
     */
    displayNoSoundWarning?: boolean;
    noSoundMessage?: string;
  };
  /**
   * How this source is published, over the app defaults (`DEFAULT_MAX_BITRATES`
   * and the per-type params of the meeting page), so a source only states what
   * it changes. Read when the OvenMedia emitter session is created: a change
   * applies to the next publish, not to a live one.
   */
  emission?: {
    /**
     * Sender params, as accepted by the SDK: `preset`, then per track
     * `mediaContentHint`, `mediaConstrains`, `rtcParams` (everything of
     * `RTCRtpSendParameters` but the encodings, e.g. degradationPreference)
     * and `rtcEncoding` (maxBitrate, maxFramerate, scaleResolutionDownBy...).
     */
    streamParams?: RTCStreamParams;
    /**
     * Options of the OvenMedia emitter session itself. Only what the SDK turns
     * into SDP rewrites (x-google bitrates, opus mono/dtx, transport-cc,
     * `a=content`) or into an ICE policy is exposed — the rest of the session
     * options is plumbing the app owns. Beware the SDK defaults: audio mono on,
     * dtx off, transport-cc on, and a `x-google-start-bitrate` of 2000.
     */
    sessionOptions?: Pick<OvenMediaEmitterWebRTCSessionOptions, "audio" | "video" | "transportCC" | "transports">;
  };
};

export const buildMediasFromSources = (
  sources: MeetingSourceExtended[],
  options?: {
    getEnabled?: (group: string) => { audio: boolean; video: boolean };
    getSourceEnabled?: (sourceName: string) => { audio: boolean; video: boolean };
  },
): MeetingSessionAddMedia<any, any, any>[] => {
  return sources.map((s) => {
    const type = s.extra.type;
    const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
    const enabled = s.name && options?.getSourceEnabled
      ? options.getSourceEnabled(s.name)
      : options?.getEnabled?.(group) ?? { audio: true, video: true };
    return {
      video: {
        deviceType: s.devices[0].type,
        enabled: enabled.video,
      },
      audio: {
        deviceType: s.devices[0].type,
        enabled: enabled.audio,
      },
      name: s.name,
      deviceId: s.id,
      extra: {
        type,
        label: s.name,
        controls: s.controls?.type,
        playerFit: s.extra.playerFit,
        width: s.extra.width,
        height: s.extra.height,
      },
    };
  });
};

//
const DEFAULT_MEETING_SOURCES: MeetingSourceExtended[] = [
  {
    name: "webcam",
    devices: [
      {
        mode: "audiovideo",
        type: MeetingSourceDeviceType.WEB,
        data: {
          mediaConstraints: {
            video: true,
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      },
    ],
    extra: {
      type: "camera",
    },
    visible: true,
  },
];

/**
 * The capture component of a web device offers its own screen sharing: it
 * swaps the device's video track for the screen, inside the source's media.
 * The meeting has a dedicated share (hooks/screenShare.ts), so that one is off
 * unless a device's settings turn it on (`disableShareScreen: false`).
 */
export const withShareScreenOffByDefault = <S extends Pick<MeetingSourceExtended, "devices">>(source: S): S => {
  if (!source.devices?.some((d) => d.type === MeetingSourceDeviceType.WEB && d.data?.disableShareScreen === undefined)) return source;
  return {
    ...source,
    devices: source.devices.map((d) => (
      d.type === MeetingSourceDeviceType.WEB && d.data?.disableShareScreen === undefined
        ? { ...d, data: { ...d.data, disableShareScreen: true } }
        : d
    )),
  };
};

/**
 * Usable sources from the settings: default `extra.type`, the capture's own
 * screen sharing off by default and unique names
 * (`selfStreams` are keyed by name). Pure — the settings objects are left
 * untouched, so the result can safely be memoized and shared.
 */
export const buildMeetingSources = (
  configured: MeetingSourceExtended[] | undefined,
): MeetingSourceExtended[] => {
  const sources = (configured ?? DEFAULT_MEETING_SOURCES).filter((s) => s.devices?.length && !s.disabled);

  const seenNames = new Set<string>();
  return sources.map((s) => {
    const baseName = s.name || "source";
    let name = baseName;
    let i = 2;
    while (seenNames.has(name)) {
      name = `${baseName}_${i++}`;
    }
    seenNames.add(name);

    return withShareScreenOffByDefault({
      ...s,
      name,
      extra: s.extra?.type ? s.extra : { ...s.extra, type: "camera" },
    });
  });
};

/**
 * Cached across hook instances so every caller sees the same objects: the SDK
 * rebuilds a source's media on the identity of the `source` prop, so a new
 * object for identical content costs the live tracks (see helpers/identity.ts).
 */
let lastBuiltSources: MeetingSourceExtended[] | undefined;

export const useMeetingSources = (): MeetingSourceExtended[] => {
  const [settings] = useSettings();
  return useMemo(() => {
    const stable = keepArrayIdentity(lastBuiltSources, buildMeetingSources(settings.sources as MeetingSourceExtended[] | undefined));
    lastBuiltSources = stable;
    return stable;
  }, [settings.sources]);
};

/**
 * Accessibility payload plus, when `canAccess` is true, the meeting's playable
 * VODs. `vods` rides on the public accessibility endpoint on purpose: it's the
 * only meeting endpoint that answers logged-out, so it's what lets a visitor
 * watch a public meeting's replays without an account.
 */
export interface MeetingAccessibilityWithVods extends MeetingAccessibilityOutputDTO {
  vods?: VodOutputDTO[];
}

/**
 * Meeting accessibility (public endpoint — works logged-in or out, and
 * evaluates the connected caller when a token is set). `id` is a meeting id,
 * a shortId, or — with `options.provider` — an external id scoped to that
 * provider. Returns `undefined` while loading and `null` on any rejection —
 * the backend answers 400 MEETING_NOT_FOUND for unknown ids, so don't test
 * for a 404.
 */
export const useMeetingAccessibility = (
  id: string | undefined,
  options?: { provider?: string; skip?: boolean },
): MeetingAccessibilityWithVods | undefined | null => {
  const sdkCtx = useSdkContext();
  const skip = options?.skip ?? false;
  const provider = options?.provider;
  const [accessibility, setAccessibility] = useState<MeetingAccessibilityWithVods | undefined | null>(undefined);
  const accessibilityRef = useRef(accessibility);
  accessibilityRef.current = accessibility;

  useEffect(() => {
    if (!id || skip) {
      setAccessibility(undefined);
      return;
    }
    // Pages canonicalize provider-scoped URLs to the resolved `_id` once
    // loaded — same meeting, new id: keep the result, don't refetch.
    if (accessibilityRef.current?._id === id) return;
    let cancelled = false;
    setAccessibility(undefined);
    sdkCtx.client.getHttpClient().meeting.getAccessibility(id, provider ? { provider } : undefined)
      .then((res) => { if (!cancelled) setAccessibility(res.data); })
      .catch(() => { if (!cancelled) setAccessibility(null); });
    return () => { cancelled = true; };
  }, [id, provider, skip]);

  return accessibility;
};

export const useMeetingVideoFeedbackSource = (): MeetingSource | undefined => {
  const [settings] = useSettings();
  return settings?.videoFeedback?.source;
};

export const useMeetingWebcamFeedbackSource = (): MeetingSourceExtended => {
  const [settings] = useSettings();
  const source = settings?.webcamFeedback?.source ?? DEFAULT_MEETING_SOURCES[0];
  return source as MeetingSourceExtended;
};

export interface MeetingContextOptions {
  ovenMediaSession?: {
    emission?: {
      options?: MeetingSessionOvenMediaEmitterManager["ovenMediaSessionOptionsExtractor"];
    };
    reception?: {
      options?: MeetingSessionOvenMediaReceiverManager["ovenMediaSessionOptionsExtractor"];
    };
  };
}

// / ! \ / ! \ / ! \ 
//
// during dev, when hot-reloading this file, the context gets recreated
// and we end up in a supposedly impossible state where we are on meetings page but with empty context
//
// Not a big deal, just needs to be known.
//
// / ! \ / ! \ / ! \ 
export const meetingsContext = createMeetingsContext();

export const useMeetingContext = () => {
  return useContext(meetingsContext);
};

export const useMeetingConfigurator = (meetingContainer: MeetingContainerAPI | MeetingContainerAPI[] | undefined, options: MeetingContextOptions) => {
  const [settings] = useSettings();
  useEffect(() => {
    if (meetingContainer === undefined) return;
    const containers = Array.isArray(meetingContainer) ? meetingContainer : [meetingContainer];
    containers.forEach((c) => {
      c.emissionManager.ovenMediaSessionOptionsExtractor = (mediaSession, mediaStream) => {
        const result: OvenMediaEmitterWebRTCSessionOptions = {
          waitingForSessionReadyTimeout: 10000,
        };
        const curr = options?.ovenMediaSession?.emission?.options?.(mediaSession, mediaStream);
        if (curr) Object.assign(result, curr);
        return result;
      };
      c.receptionManager.ovenMediaSessionOptionsExtractor = (session: MeetingSession, mediaSession: MeetingSessionMedia) => {
        const result: OvenMediaReceiverWebRTCSessionOptions = {};
        const curr = options?.ovenMediaSession?.reception?.options?.(session, mediaSession);
        if (curr) Object.assign(result, curr);
        return result;
      };
    });
  }, [settings, meetingContainer, options]);
};

export const useMeetingDataOnChange = <T>(
  state: MeetingContainerAPI | undefined,
  selector: (state: MeetingContextData) => T,
  callback: (value: T, previous: T) => void
) => {
  useMeetingContextDataOnChange(state, selector, callback);
};

/**
 * Existing medias as a `setMedias` payload: by id only. The backend merges
 * whatever else is sent into the stored media, and the read-side fields
 * (`createdAt`, ...) fail its cast.
 */
export const keepMedias = (medias: MeetingSessionMedia[]) => medias.map((m) => ({ id: m.id }));

interface StreamData {
  stream: MediaStream;
  ready: boolean;
}

export const useDynamicMedia = (
  meetingContainer: MeetingContainerAPI | undefined,
  callbacks?: {
    onStreamStart?: (name: string, success: boolean) => void;
  }
) => {
  const session = useMeetingContextDataSelector(meetingContainer, (state) => state.session);

  const streamDataRef = useRef<Record<string, StreamData>>({});
  const [, setForceUpdate] = useState({});

  // there was issues when storing streams in a state, the "set" function is being called too quickly
  // problem solved with a ref but we still need to force renders for the get() function
  const update = () => setForceUpdate({});

  const startStreamOnMedia = async (name: string, media: MeetingSessionMedia, streamData: StreamData, params?: RTCStreamParams): Promise<boolean> => {
    if (!meetingContainer) return false;
    streamData.ready = false;
    const success = await meetingContainer.setMediaStream(media.id, streamData.stream, params);
    streamData.ready = true;
    if (!success) return false;
    callbacks?.onStreamStart?.(name, !!success);
    return true;
  };

  useEffect(() => {
    if (!meetingContainer) return;
    const ovenMediaSessionListener = meetingContainer.emissionManager.addEventListener("create", (s) => {
      const mediaName = s.data.mediaSession.name;
      if (!mediaName) return;

      if (!streamDataRef.current[mediaName]) return;

      // Deferred past the SDK's own start: fired synchronously on creation,
      // before that start, which has no stream, fails and schedules a retry.
      // Attached now, the stream got published once by us, then once more by
      // that retry — a second PUBLISHING landing after the READY once timers
      // are throttled (tab in the background, e.g. sharing another tab).
      // Attached after, it finds the retry pending and leaves the one start to it.
      setTimeout(() => {
        const streamData = streamDataRef.current[mediaName];
        if (!streamData) return;
        startStreamOnMedia(mediaName, s.data.mediaSession, streamData);
      }, 0);
    });
    return () => {
      ovenMediaSessionListener?.flush();
    };
  }, [meetingContainer, callbacks]);

  const set = async (
    name: string,
    stream: MediaStream,
    additionalData?: {
      extraType?: string;
      deviceId?: string;
      /** Merged into the media's `extra`, next to `type`. */
      extra?: Record<string, any>;
      /** Initial state, instead of following the medias already emitted. */
      enabled?: { audio?: boolean; video?: boolean };
    },
    params?: RTCStreamParams
  ): Promise<boolean> => {
    if (!meetingContainer) return false;
    if (!session) return false;

    const currentStreamData = streamDataRef.current[name];

    if (currentStreamData) {
      if (!currentStreamData.ready) return false;
      const currentMedia = session.medias.find((m) => m.name === name);
      if (currentMedia) {
        // stream has been set and a media exists remotely
        currentStreamData.stream = stream;
        update();
        return startStreamOnMedia(name, currentMedia, currentStreamData, params);
      }
      return false; // a stream with this name is already waiting for a media
    }

    streamDataRef.current[name] = { stream, ready: false };
    update();

    const { deviceId, extraType, extra, enabled } = additionalData ?? {};
    const result = await meetingContainer.updater.setMedias(session.id, [
      ...keepMedias(session.medias),
      {
        video: {
          deviceType: "camera",
          enabled: enabled?.video ?? meetingContainer.emissionManager.videoIsEnabled({ mode: "some" }),
        },
        audio: {
          deviceType: "audio",
          enabled: enabled?.audio ?? meetingContainer.emissionManager.audioIsEnabled({ mode: "some" }),
        },
        name: name,
        deviceId,
        extra: extraType || extra
          ? {
              ...extra,
              type: extraType ?? extra?.type,
            }
          : undefined,
      },
    ]);
    if (result?.error || !result?.response) {
      console.error(`Could not set media "${name}" : `, result?.error);
      remove(name);
      return false;
    }

    const streamData = streamDataRef.current[name];
    if (!streamData) return false; // removed while the media was being created
    streamData.ready = true;

    return true;
  };

  // TODO : add a way to update the stream
  const get = (name: string) => streamDataRef.current[name]?.stream;

  const remove = async (name: string): Promise<boolean> => {
    if (!meetingContainer) return false;
    if (!session) return false;
    if (!streamDataRef.current[name]) return false;

    delete streamDataRef.current[name];
    update();

    // TODO : what if this fails, we need to mark it as needing to be removed
    const result = await meetingContainer.updater.setMedias(
      session.id,
      keepMedias(session.medias.filter((m) => m.name !== name))
    );
    if (result?.error || !result?.response) {
      console.error(`Could not remove media "${name}" : `, result?.error);
      return false;
    }

    return true;
  };

  return {
    set,
    get,
    remove,
  };
};

export const useHasOngoingGroups = (context: MeetingContainerAPI | undefined): boolean => {
  const rooms = useMeetingContextDataSelector(context, (state) => state.rooms);
  return useMemo(() => {
    const match = rooms?.some((r) => r.type === MeetingRoomType.Group && r.status === MeetingRoomStatus.Ongoing);
    return !!match;
  }, [rooms]);
};

export const RestrictedAudioPermission = "[[role;>=;moderator]||[highlighting;==;ongoing]]";

export const usePermission = (meeting: Meeting | undefined) => {
  const sdkCtx = useSdkContext();
  return useMemo(() => {
    const hasPermission = (category: keyof MeetingPermissions, permission: string) => {
      return meeting?.permissions?.[category]?.includes(permission);
    };

    const setPermission = async (category: keyof MeetingPermissions, permission: string, enabled: boolean) => {
      const meetingId = meeting?.id;
      if (!meetingId) return;

      const newPermissions = { ...(meeting.permissions ?? {}) };
      const categoryPermissions = new Set<string>(meeting.permissions?.[category] ?? []);

      if (enabled) categoryPermissions.add(permission);
      else categoryPermissions.delete(permission);
      newPermissions[category] = Array.from(categoryPermissions);

      const websocket = sdkCtx.client.getWebsocketClient();
      const result = await websocket.emitter.meeting.updateById({ id: meetingId, permissions: newPermissions });
      if (!result.response || result.error) {
        toastError("Une erreur est survenue...");
        return;
      }
    };

    const addPermission = async (category: keyof MeetingPermissions, permission: string) => setPermission(category, permission, true);

    const removePermission = async (category: keyof MeetingPermissions, permission: string) => setPermission(category, permission, false);

    return { hasPermission, setPermission, addPermission, removePermission };
  }, [meeting])
};

/**
 * - the webcam management API is handled on the expert meeting level, wrapping all
 * the possible expert pages
 * - if we pass from one view requiring the expert webcam to an other one requiring it too,
 * we now longer have to delete and set the expert_webcam, which leads to errors
 */

export const useDynamicWebcam = (meetingContainerAPI: MeetingContainerAPI | undefined, name: string, stream?: MediaStream) => {
  const dynamicMediaAPI = useDynamicMedia(meetingContainerAPI);

  useEffect(() => {
    if (stream) {
      dynamicMediaAPI.set(name, stream, { extraType: "camera" });
    } else {
      dynamicMediaAPI.remove(name);
    }
  }, [dynamicMediaAPI, stream]);

  useEffect(() => {
    return () => {
      dynamicMediaAPI.remove(name);
    };
  }, []);
};

export const useEmptyMediaStream = (enabled: boolean = false): MediaStream | undefined => {
  const videoTrackRef = useRef<MediaStreamEmptyVideoTrack>();
  const audioTrackRef = useRef<MediaStreamSilentTrack>();
  const [mediaStream, setMediaStream, mediaStreamRef] = useStateWithRef<MediaStream>();

  const generateVideoTrack = () => {
    let result: MediaStreamEmptyVideoTrack | undefined = undefined;
    try {
      result = new MediaStreamEmptyVideoTrack();
    } catch (err) {}
    return result;
  };

  const generateAudioTrack = () => {
    let result: MediaStreamSilentTrack | undefined = undefined;
    try {
      result = new MediaStreamSilentTrack(getAudioContextForVolumeAnalyzer());
    } catch (err) {}
    return result;
  };

  const generateMediaStream = () => {
    if (!videoTrackRef.current) videoTrackRef.current = generateVideoTrack();
    if (!audioTrackRef.current) audioTrackRef.current = generateAudioTrack();

    const videoTrack = videoTrackRef.current?.track;
    const audioTrack = audioTrackRef.current?.track;
    if (!videoTrack || !audioTrack) return undefined;
    return new MediaStream([videoTrack, audioTrack]);
  };

  const clean = () => {
    videoTrackRef.current?.destroy();
    audioTrackRef.current?.destroy();
    if (mediaStreamRef.current) destroyMediaStream(mediaStreamRef.current);
  };

  useEffect(() => {
    if (enabled) {
      setMediaStream(generateMediaStream())
    }
    return () => clean();
  }, [enabled]);

  return mediaStream;
};
